import { useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';
import LoginPage from './pages/auth/LoginPage';
import DashboardsPage from './pages/dashboards/DashboardsPage';
import CampaignsPage from './pages/campaigns/CampaignsPage';
import CampaignDetailPage from './pages/campaigns/CampaignDetailPage';
import CreateCampaignPage from './pages/campaigns/CreateCampaignPage';
import AdSetsPage from './pages/adsets/AdSetsPage';
import CreateAdSetPage from './pages/adsets/CreateAdSetPage';
import AdSetDetailPage from './pages/adsets/AdSetDetailPage';
import AdsPage from './pages/ads/AdsPage';
import AdDetailPage from './pages/ads/AdDetailPage';
import CreateAdPage from './pages/ads/CreateAdPage';
import CreativesPage from './pages/creatives/CreativesPage';
import StudioPage from './pages/studio/StudioPage';
import AdsManagerPage from './pages/ads-manager/AdsManagerPage';
import CreativeGenerationPage from './pages/creative-generation/CreativeGenerationPage';
import RulesPage from './pages/rules/RulesPage';
import AlertChannelsPage from './pages/alert-channels/AlertChannelsPage';
import SettingsPage from './pages/settings/SettingsPage';
import UsersPage from './pages/users/UsersPage';
import AdAccountsPage from './pages/ad-accounts/AdAccountsPage';
import PagesPage from './pages/pages/PagesPage';
import NetworksPage from './pages/networks/NetworksPage';
import NetworkDetailPage from './pages/networks/NetworkDetailPage';
import PixelsPage from './pages/pixels/PixelsPage';
import TrackingPage from './pages/tracking/TrackingPage';
import ConversionsTabsPage from './pages/tracking/ConversionsTabsPage';
import ClicksPage from './pages/tracking/ClicksPage';
import LandingPagesPage from './pages/landing-pages/LandingPagesPage';
import RuleGroupsPage from './pages/flows/RuleGroupsPage';
import TrafficSourcesPage from './pages/traffic-sources/TrafficSourcesPage';
import TrafficSourceDetailPage from './pages/traffic-sources/TrafficSourceDetailPage';
import TagsPage from './pages/tags/TagsPage';
import PostbacksPage from './pages/postbacks/PostbacksPage';
import ConversionTypesPage from './pages/conversion-types/ConversionTypesPage';
import TSIntegrationsPage from './pages/ts-integrations/TSIntegrationsPage';
import PartnersPage from './pages/partners/PartnersPage';
import AuditLogPage from './pages/audit/AuditLogPage';
import McpConnectionsPage from './pages/mcp-connections/McpConnectionsPage';
import TeamActivityPage from './pages/team-activity/TeamActivityPage';
import ProfitSharePage from './pages/profit-share/ProfitSharePage';
import ProfitShareWorkspacePage from './pages/profit-share/ProfitShareWorkspacePage';
import UserActivityPage from './pages/team-activity/UserActivityPage';
import { doRefresh } from './services/api';
import StaleAppWarning from './components/StaleAppWarning';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { accessToken, logout } = useAuthStore();
  const [recovering, setRecovering] = useState(!accessToken);

  useEffect(() => {
    if (accessToken) {
      setRecovering(false);
      return;
    }

    // The token is memory-only, so this runs on every load — and gating render on
    // it is what lets the refresh seed the store before any report mounts.
    doRefresh().catch(() => {
      // Without logout(), `isAuthenticated` stays set and AuthLayout redirects
      // straight back here from /login — the two loop forever.
      logout();
      setRecovering(false);
    });
  }, [accessToken, logout]);

  if (recovering) {
    return null; // Brief blank while attempting refresh — avoids login flash
  }

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

const router = createBrowserRouter([
  // Auth routes
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
    ],
  },
  // Protected routes
  {
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: <Navigate to="/tracking" replace /> },
      { path: '/dashboard', element: <Navigate to="/dashboards" replace /> },
      { path: '/dashboards', element: <DashboardsPage /> },
      { path: '/campaigns', element: <CampaignsPage /> },
      { path: '/campaigns/new', element: <CreateCampaignPage /> },
      { path: '/campaigns/:id', element: <CampaignDetailPage /> },
      { path: '/campaigns/:id/edit', element: <CreateCampaignPage /> },
      { path: '/adsets', element: <AdSetsPage /> },
      { path: '/adsets/new', element: <CreateAdSetPage /> },
      { path: '/adsets/:id', element: <AdSetDetailPage /> },
      { path: '/adsets/:id/edit', element: <CreateAdSetPage /> },
      { path: '/ads', element: <AdsPage /> },
      { path: '/ads/new', element: <CreateAdPage /> },
      { path: '/ads/:id', element: <AdDetailPage /> },
      { path: '/ads/:id/edit', element: <CreateAdPage /> },
      { path: '/creatives', element: <CreativesPage /> },
      { path: '/studio', element: <StudioPage /> },
      { path: '/studio/:projectId', element: <StudioPage /> },
      { path: '/ads-manager', element: <AdsManagerPage /> },
      { path: '/creative-generation/:runId', element: <CreativeGenerationPage /> },
      { path: '/rules', element: <RulesPage /> },
      { path: '/alert-channels', element: <AlertChannelsPage /> },
      { path: '/networks', element: <NetworksPage /> },
      { path: '/networks/new', element: <NetworkDetailPage /> },
      { path: '/networks/:id/duplicate', element: <NetworkDetailPage /> },
      { path: '/networks/:id', element: <NetworkDetailPage /> },
      { path: '/tracking', element: <TrackingPage /> },
      { path: '/conversions', element: <ConversionsTabsPage /> },
      { path: '/clicks', element: <ClicksPage /> },
      { path: '/landing-pages', element: <LandingPagesPage /> },
      { path: '/rule-groups', element: <RuleGroupsPage /> },
      { path: '/traffic-sources', element: <TrafficSourcesPage /> },
      { path: '/traffic-sources/new', element: <TrafficSourceDetailPage /> },
      { path: '/traffic-sources/:id/duplicate', element: <TrafficSourceDetailPage /> },
      { path: '/traffic-sources/:id', element: <TrafficSourceDetailPage /> },
      { path: '/tags', element: <TagsPage /> },
      { path: '/postbacks', element: <PostbacksPage /> },
      { path: '/conversion-types', element: <ConversionTypesPage /> },
      { path: '/ts-integrations', element: <TSIntegrationsPage /> },
      { path: '/partners', element: <PartnersPage /> },
      { path: '/ad-accounts', element: <AdAccountsPage /> },
      { path: '/users', element: <UsersPage /> },
      { path: '/pages', element: <PagesPage /> },
      { path: '/pixels', element: <PixelsPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/audit', element: <AuditLogPage /> },
      { path: '/mcp-connections', element: <McpConnectionsPage /> },
      { path: '/team-activity', element: <TeamActivityPage /> },
      { path: '/profit-share', element: <ProfitSharePage /> },
      { path: '/profit-share/:workspaceId', element: <ProfitShareWorkspacePage /> },
      { path: '/team-activity/:userId', element: <UserActivityPage /> },
    ],
  },
  // Fallback
  { path: '*', element: <Navigate to="/" replace /> },
]);

function App() {
  return (
    <>
      <StaleAppWarning />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
